import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_NAME = 'ZO Werbefläche';
const DEFAULT_DESCRIPTION = 'Mobile Werbeflächen mieten im Zürcher Oberland – Wetzikon, Uster, Pfäffikon, Hinwil und Umgebung. Werbeanhänger an Top-Standorten buchen, ab CHF 350/Woche.';
const DEFAULT_IMAGE = '/api/static/images/hero-trailer.jpg';
const BASE_URL = typeof window !== 'undefined' ? window.location.origin : '';

const SEO = ({
  title,
  description = DEFAULT_DESCRIPTION,
  image = DEFAULT_IMAGE,
  url,
  type = 'website',
  noindex = false,
  structuredData,
  canonicalPath,
  lang = 'de',
}) => {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} – Mobile Werbeflächen im Zürcher Oberland`;
  const canonicalUrl = canonicalPath ? `${BASE_URL}${canonicalPath}` : url || (typeof window !== 'undefined' ? window.location.href : '');
  const languages = ['de', 'en', 'fr', 'it'];

  return (
    <Helmet>
      {/* Basics */}
      <html lang={lang} />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="de_CH" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* hreflang for multilingual */}
      {canonicalPath && languages.map((lng) => (
        <link key={lng} rel="alternate" hrefLang={lng} href={`${BASE_URL}${canonicalPath}?lang=${lng}`} />
      ))}
      {canonicalPath && <link rel="alternate" hrefLang="x-default" href={`${BASE_URL}${canonicalPath}`} />}

      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

// Pre-built structured data generators
export const localBusinessSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  'name': 'ZO Werbefläche',
  'description': 'Mobile Werbeflächen und Werbeanhänger mieten im Zürcher Oberland – Wetzikon, Uster, Pfäffikon, Hinwil, Bubikon, Rüti und Umgebung. Flexible Buchung an Top-Standorten mit hoher Passantenfrequenz.',
  'url': BASE_URL,
  'image': DEFAULT_IMAGE,
  'address': {
    '@type': 'PostalAddress',
    'addressRegion': 'Zürich',
    'addressCountry': 'CH',
    'addressLocality': 'Wetzikon',
    'postalCode': '8620'
  },
  'geo': {
    '@type': 'GeoCoordinates',
    'latitude': 47.3260,
    'longitude': 8.7978
  },
  'areaServed': [
    { '@type': 'City', 'name': 'Wetzikon' },
    { '@type': 'City', 'name': 'Uster' },
    { '@type': 'City', 'name': 'Pfäffikon ZH' },
    { '@type': 'City', 'name': 'Hinwil' },
    { '@type': 'City', 'name': 'Bubikon' },
    { '@type': 'City', 'name': 'Rüti ZH' },
    { '@type': 'City', 'name': 'Volketswil' },
    { '@type': 'City', 'name': 'Dübendorf' },
    { '@type': 'City', 'name': 'Illnau-Effretikon' },
    { '@type': 'City', 'name': 'Gossau ZH' },
    { '@type': 'City', 'name': 'Grüningen' },
    { '@type': 'City', 'name': 'Bauma' },
    { '@type': 'City', 'name': 'Wald ZH' },
    {
      '@type': 'GeoCircle',
      'geoMidpoint': { '@type': 'GeoCoordinates', 'latitude': 47.3260, 'longitude': 8.7978 },
      'geoRadius': '25000'
    }
  ],
  'priceRange': 'CHF 350 - CHF 1200',
  'currenciesAccepted': 'CHF',
  'paymentAccepted': 'Kreditkarte, Stripe',
  'sameAs': []
});

export const serviceSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  'serviceType': 'Werbeflächen-Vermietung',
  'name': 'Mobile Werbeflächen mieten – Zürcher Oberland',
  'description': 'Mieten Sie mobile Werbeanhänger an stark frequentierten Standorten in Wetzikon, Uster, Pfäffikon, Hinwil und im gesamten Zürcher Oberland. Inklusive optionalem Druckservice.',
  'provider': {
    '@type': 'LocalBusiness',
    'name': 'ZO Werbefläche'
  },
  'areaServed': {
    '@type': 'Place',
    'name': 'Zürcher Oberland, Schweiz'
  },
  'offers': {
    '@type': 'Offer',
    'priceCurrency': 'CHF',
    'price': '350',
    'priceSpecification': {
      '@type': 'UnitPriceSpecification',
      'price': '350',
      'priceCurrency': 'CHF',
      'unitText': 'Woche'
    },
    'availability': 'https://schema.org/InStock'
  }
});

export const faqSchema = (faqs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  'mainEntity': faqs.map(faq => ({
    '@type': 'Question',
    'name': faq.question,
    'acceptedAnswer': {
      '@type': 'Answer',
      'text': faq.answer
    }
  }))
});

export const breadcrumbSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  'itemListElement': items.map((item, index) => ({
    '@type': 'ListItem',
    'position': index + 1,
    'name': item.name,
    'item': `${BASE_URL}${item.path}`
  }))
});

export const competitionSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Event',
  'name': 'Wettbewerb – 1 Woche GRATIS Werbefläche',
  'description': 'Nehmen Sie an unserem Wettbewerb teil und gewinnen Sie eine Woche kostenlose Werbefläche im Zürcher Oberland. Ab 100 Teilnehmern wird automatisch ein Gewinner ausgelost.',
  'eventAttendanceMode': 'https://schema.org/OnlineEventAttendanceMode',
  'organizer': {
    '@type': 'Organization',
    'name': 'ZO Werbefläche'
  },
  'offers': {
    '@type': 'Offer',
    'price': '0',
    'priceCurrency': 'CHF',
    'availability': 'https://schema.org/InStock'
  },
  'location': {
    '@type': 'VirtualLocation',
    'url': `${BASE_URL}/wettbewerb`
  }
});

export default SEO;
